/**
 * Admin · org drill-in (GET /admin/orgs/{orgId}/detail).
 * Economics + members + usage, plus two owner actions: provision a super-admin (temp password
 * shown ONCE) and policy-gated "act as company" impersonation (reason + duration → scoped token).
 */
import { useState } from "react";
import type { ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../lib/api.js";
import { useAuth } from "../../lib/AuthContext.js";
import { getSession, setSession } from "../../lib/auth.js";
import { currencyMinor, inrCompact, usdCompact } from "../../lib/format.js";
import type { AllowanceUnit, BillingCurrency, OrgKind, RankingBasis } from "@talenthop/shared";
import { RANKING_BASIS_LABEL } from "@talenthop/shared";
import { Arrow, Alert, Info, ShieldCheck, Users } from "../../components/icons.js";
import { Modal, Switch, apiErrorText } from "./adminUi.js";
import { KIND_LABEL, KIND_PILL } from "./orgKind.js";
import { OrgActivityLog } from "./OrgActivityLog.js";

interface OrgDetailPayload {
  org: {
    id: string;
    name: string;
    type: string;
    region: string;
    kind?: OrgKind;
    supportAccessAllowed: boolean;
    staffAugEnabled: boolean;
    /** What this org's candidate lists, buckets and headline rank by (0105). Absent on an older API = interview. */
    rankingBasis?: RankingBasis;
    walletUnits: number;
    /** Plan allowances (unexpired lots). Absent on an older API → the allowance card hides. */
    allowances?: Record<AllowanceUnit, AllowanceUsage>;
    /** Owner-controlled per-org feature flags (migration 0075) — gate the org-facing rescore
     *  button, per-requisition judge-prompt editor, and transcript-copy button. Write side:
     *  PATCH /admin/orgs/{orgId}/flags (adminOrgDetail.ts). Optional: absent (or a mid-deploy API
     *  instance that predates this field) degrades to the same defaults the DB migration ships —
     *  every flag off except copyQuestionsEnabled — rather than a TypeError. */
    flags?: { rescoreEnabled: boolean; customPromptEnabled: boolean; copyQuestionsEnabled: boolean };
    createdAt: string;
  };
  members: { userId: string; name: string; email: string; role: string; status: string }[];
  requisitionsByState: { state: string; count: number }[];
  /** The org's locked billing currency — its revenue is real money only in THIS currency. */
  currency: BillingCurrency;
  /** MTD paid credit purchases in the org currency's MINOR units — same query as Revenue's rollup. */
  revenueMinor: number;
  /** ≈ INR at FX_TO_INR — a reporting axis, never a billed amount. */
  revenueInrEq: number;
  cogsInr: number;
  /** null when the org collected nothing this cycle — no revenue, no meaningful margin. */
  marginPct: number | null;
  // Authoritative count of billed verified interviews (kind='verified_interview') for this cycle.
  // Distinct from usageByLevel counts, which sum ALL ledger kinds (scans, scheduled, sourcing, …)
  // and therefore over-report if used as a "verified" tally.
  verified: number;
  // usage_ledger amounts are CREDIT UNITS (0031), not rupees — never run them through a
  // currency formatter.
  usageByLevel: { level: string; count: number; credits: number }[];
  recentActivity: { label: string; ts: string }[];
  // Wave 3 staff-aug links, carried on the detail payload itself: `vendors` = staffing
  // firms this org (end_customer) buys from; `clients` = end_customers this org (vendor) fulfils for.
  vendors?: LinkedOrg[];
  clients?: LinkedOrg[];
}

interface AllowanceUsage {
  total: number;
  used: number;
  remaining: number;
  nextExpiry: string | null;
}
const ALLOWANCE_LABEL: Record<AllowanceUnit, string> = { job: "Jobs", scan: "Resumes", interview: "AI interviews" };

interface DetailResponse {
  detail: OrgDetailPayload;
}

interface ProvisionResponse {
  provisioned: { email: string; name: string; invited: boolean };
}

interface ResendResponse {
  resent: { email: string; invited: boolean };
}

interface ImpersonateResponse {
  accessToken: string;
  tokenType: string;
  scopedOrgId: string;
  expiresInMinutes: number;
}

/** A linked org in the vendor↔client relationship (either direction). */
interface LinkedOrg {
  id: string;
  name: string;
  kind?: OrgKind;
  region?: string;
}
interface VendorsResponse {
  vendors?: LinkedOrg[]; // orgs that fulfil staff-aug FOR this org (this org = end_customer)
}

/** GET /orgs summary — used to populate the "Link a vendor" picker with vendor-kind orgs. */
interface OrgSummary {
  id: string;
  name: string;
  kind?: OrgKind;
  // 0049: whether the org accepted the CURRENT data terms — read-only here.
  dataTermsAccepted?: boolean;
  dataTermsVersion?: string;
}
interface OrgsResponse {
  orgs: OrgSummary[];
}

const REGION_LABEL: Record<string, string> = {
  "in-central": "India",
  "us-east": "US East",
  "eu-west": "EU West",
};

export function OrgDetail(): ReactNode {
  const { orgId = "" } = useParams();
  const queryClient = useQueryClient();
  const { me } = useAuth();
  const isOwner = me?.role === "owner";

  const { data, isLoading, isError } = useQuery({
    queryKey: ["admin-org-detail", orgId],
    queryFn: () => api.get<DetailResponse>(`/admin/orgs/${orgId}/detail`),
    enabled: orgId.length > 0,
  });
  const detail = data?.detail;

  // --- provision super-admin (temp password shown once) ---
  const [provName, setProvName] = useState("");
  const [provEmail, setProvEmail] = useState("");
  const provision = useMutation({
    mutationFn: () =>
      api.post<ProvisionResponse>(`/admin/orgs/${orgId}/provision-admin`, {
        name: provName.trim(),
        email: provEmail.trim(),
      }),
    onSuccess: () => {
      setProvName("");
      setProvEmail("");
      void queryClient.invalidateQueries({ queryKey: ["admin-org-detail", orgId] });
    },
  });

  // --- resend a set-password invite (owner-only) — re-issues the token + re-emails the link ---
  const resend = useMutation({
    mutationFn: (email: string) =>
      api.post<ResendResponse>(`/admin/orgs/${orgId}/resend-invite`, { email }),
  });

  // --- support-access toggle (gates "act as company") ---
  const supportAccess = useMutation({
    mutationFn: (allowed: boolean) =>
      api.patch<{ supportAccessAllowed: boolean }>(`/admin/orgs/${orgId}/support-access`, { allowed }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin-org-detail", orgId] });
    },
  });

  // Owner credit grant. The endpoint has existed since 0031 but had no screen, so topping an org
  // up meant calling it by hand. It refuses unless ALLOW_CREDIT_GRANTS=true, which is why the card
  // below states plainly that a grant is credits nobody paid for.
  const [grantUnits, setGrantUnits] = useState("");
  const grant = useMutation({
    mutationFn: (units: number) =>
      api.post<{ ok: boolean; balance: number }>(`/admin/orgs/${orgId}/credit-units`, { units }),
    onSuccess: () => {
      setGrantUnits("");
      void queryClient.invalidateQueries({ queryKey: ["admin-org-detail", orgId] });
    },
  });

  // Owner allowance grant — jobs / resumes / AI interviews. Credit units can't unblock New
  // requirement (it gates on the job allowance), so a pilot that needs more jobs needs this.
  const [allowUnit, setAllowUnit] = useState<AllowanceUnit>("job");
  const [allowQty, setAllowQty] = useState("");
  const allowGrant = useMutation({
    mutationFn: (v: { unit: AllowanceUnit; qty: number }) =>
      api.post<{ ok: boolean; changed: number; allowances: Record<AllowanceUnit, AllowanceUsage> }>(
        `/admin/orgs/${orgId}/allowances`,
        v,
      ),
    onSuccess: () => {
      setAllowQty("");
      void queryClient.invalidateQueries({ queryKey: ["admin-org-detail", orgId] });
    },
  });

  // 0040: staff augmentation is an entitlement, not a default. Same shape as supportAccess.
  const staffAugMut = useMutation({
    mutationFn: (enabled: boolean) =>
      api.patch<{ staffAugEnabled: boolean }>(`/admin/orgs/${orgId}/staff-aug`, { enabled }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin-org-detail", orgId] });
    },
  });

  // What this org's candidate lists, buckets and headline rank by. Read at request time, so flipping it
  // re-ranks everything with no re-scoring and no model call.
  const rankingBasisMut = useMutation({
    mutationFn: (basis: RankingBasis) => api.patch<{ rankingBasis: RankingBasis }>(`/admin/orgs/${orgId}/ranking-basis`, { basis }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin-org-detail", orgId] });
    },
  });

  // One switch per org — the rescore button, per-requisition judge-prompt editor, and
  // transcript-copy button (candidacyRescore.ts, requisitionPrompt.ts, Review.tsx) all read these.
  const flagsMut = useMutation({
    mutationFn: (patch: Partial<OrgDetailPayload["org"]["flags"]>) =>
      api.patch<{ flags: OrgDetailPayload["org"]["flags"] }>(`/admin/orgs/${orgId}/flags`, patch),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin-org-detail", orgId] });
    },
  });

  // --- impersonation ("act as company") ---
  const [impOpen, setImpOpen] = useState(false);
  const [impReason, setImpReason] = useState("");
  const [impDuration, setImpDuration] = useState(30);
  const impersonate = useMutation({
    mutationFn: () =>
      api.post<ImpersonateResponse>("/admin/impersonate", {
        orgId,
        reason: impReason.trim(),
        durationMinutes: impDuration,
      }),
    onSuccess: (res) => {
      // Keep the real admin session so the banner can restore it, then swap in the scoped token.
      const orig = getSession();
      if (orig) localStorage.setItem("th_auth_orig", JSON.stringify(orig));
      localStorage.setItem(
        "th_imp",
        JSON.stringify({
          orgId: res.scopedOrgId,
          orgName: detail?.org.name ?? "client org",
          expiresAt: new Date(Date.now() + res.expiresInMinutes * 60_000).toISOString(),
        }),
      );
      // persona "user" is a routing hint only — the token itself stays admin + org-scoped.
      setSession({
        accessToken: res.accessToken,
        refreshToken: "",
        tokenType: res.tokenType,
        persona: "user",
      });
      window.location.assign("/app");
    },
  });

  // --- vendor linking (org_relationships) ---
  const orgKind = detail?.org.kind;
  const staffAug = detail?.org.staffAugEnabled ?? false;
  // Same defaults the DB migration ships (0075) — degrades gracefully rather than a TypeError
  // against an API instance mid-deploy that predates this field.
  const flags = detail?.org.flags ?? {
    rescoreEnabled: false,
    customPromptEnabled: false,
    copyQuestionsEnabled: true,
  };
  const wallet = detail?.org.walletUnits ?? 0;
  const grantN = Number.parseInt(grantUnits, 10);
  const grantValid = Number.isInteger(grantN) && grantN !== 0 && Math.abs(grantN) <= 100000;
  const allowN = Number.parseInt(allowQty, 10);
  const allowValid = Number.isInteger(allowN) && allowN !== 0 && Math.abs(allowN) <= 10000;
  const isVendorOrg = orgKind === "vendor";
  // Only the end_customer card consumes this (the list of staffing firms it buys from); it
  // also gives the link action a narrow cache key to invalidate. Vendor orgs read their
  // client list straight off the /detail payload, so they don't need this call.
  const vendorsQuery = useQuery({
    queryKey: ["admin-org-vendors", orgId],
    queryFn: () => api.get<VendorsResponse>(`/admin/orgs/${orgId}/vendors`),
    enabled: orgId.length > 0 && orgKind === "end_customer",
  });
  const linkedVendors = vendorsQuery.data?.vendors ?? [];
  // Clients (end_customers this vendor fulfils for) come from the /detail payload — the
  // /vendors endpoint only returns the reverse direction, so reading it here always yielded [].
  const linkedClients = detail?.clients ?? [];

  // The /orgs summary serves two reads here: the vendor-picker options (vendor-kind orgs,
  // minus self and already-linked) and, since 0049, this org's data-terms acceptance state —
  // so it is fetched for every org kind now, not just end customers.
  const orgsQuery = useQuery({
    queryKey: ["orgs"],
    queryFn: () => api.get<OrgsResponse>("/orgs"),
    enabled: orgId.length > 0,
  });
  const thisOrgSummary = orgsQuery.data?.orgs.find((o) => o.id === orgId);
  const linkedIds = new Set(linkedVendors.map((v) => v.id));
  const vendorOptions = (orgsQuery.data?.orgs ?? []).filter(
    (o) => o.kind === "vendor" && o.id !== orgId && !linkedIds.has(o.id),
  );

  const [pickVendorId, setPickVendorId] = useState("");
  const linkVendor = useMutation({
    mutationFn: (vendorOrgId: string) =>
      api.post(`/admin/orgs/${orgId}/vendors`, { vendorOrgId }),
    onSuccess: () => {
      setPickVendorId("");
      void queryClient.invalidateQueries({ queryKey: ["admin-org-vendors", orgId] });
    },
  });

  return (
    <section>
      <div className="page-h">
        <div>
          <h1 style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {detail?.org.name ?? "Client org"}
            {orgKind && <span className={KIND_PILL[orgKind]}>{KIND_LABEL[orgKind]}</span>}
          </h1>
          <div className="sub">Org drill-in · health, billing & margin metadata only · live from the API</div>
        </div>
        <Link className="btn sm ghost" to="/admin/orgs">
          <span style={{ display: "inline-flex", transform: "rotate(180deg)" }}>
            <Arrow />
          </span>{" "}
          All orgs
        </Link>
      </div>

      {isLoading && <div className="card pad mut sm">Loading org detail…</div>}

      {isError && (
        <div className="banner crit">
          <span>Couldn't load this org. Please retry in a moment.</span>
        </div>
      )}

      {detail && (
        <>
          <div className="grid g4" style={{ marginBottom: 16 }}>
            <div className="card pad stat">
              <div className="l">Revenue · MTD</div>
              {/* Real money in the org's OWN currency (paid credit purchases, same query as the
                  Revenue rollup); the ₹-eq below is the FX_TO_INR reporting axis. */}
              <div className="n violet tnum">{currencyMinor(detail.revenueMinor, detail.currency)}</div>
              <div className="d" style={{ textTransform: "capitalize" }}>
                ≈ {inrCompact(detail.revenueInrEq)} · ≈ {usdCompact(detail.revenueInrEq)} ·{" "}
                {REGION_LABEL[detail.org.region] ?? detail.org.region}
              </div>
            </div>
            <div className="card pad stat">
              <div className="l">Spend (COGS)</div>
              <div className="n tnum">{inrCompact(detail.cogsInr)}</div>
              <div className="d">≈ {usdCompact(detail.cogsInr)} · what we spend on this org</div>
            </div>
            <div className="card pad stat">
              <div className="l">Margin</div>
              {/* null = no revenue, so no margin to state; and a negative margin must never
                  render in the green "good" style. */}
              <div
                className={`n tnum ${
                  detail.marginPct == null ? "" : detail.marginPct >= 0 ? "good" : "crit"
                }`}
              >
                {detail.marginPct == null ? "—" : `${detail.marginPct}%`}
              </div>
              <div className="d">contribution · MTD</div>
            </div>
            <div className="card pad stat">
              <div className="l">Verified candidates</div>
              {/* Authoritative verified count from the API (kind='verified_interview'). Do NOT sum
                  usageByLevel here — that mixes in scans/scheduled/sourcing and over-reports. */}
              <div className="n teal tnum">{detail.verified.toLocaleString("en-IN")}</div>
              <div className="d">verified interviews · this cycle</div>
            </div>
          </div>

          {/* 0049: read-only acceptance state of the data terms (docs/DATA_TERMS.md). No action
              here on purpose — only the org's own admins can accept, never the platform.
              The in-app banner that used to prompt for it was removed 2026-07-31: consent moves
              into the first-login terms & conditions for a new org's super admin, so nobody is
              nagged mid-task. Until that ships nothing can accept, so expect "Not accepted" —
              which is honest, and keeps the training gate closed. */}
          <div className="card pad" style={{ marginBottom: 16, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
            <span className="mut sm">Data terms · service improvement &amp; model training</span>
            {thisOrgSummary?.dataTermsAccepted ? (
              <span className="pill v">Accepted · {thisOrgSummary.dataTermsVersion ?? "current"}</span>
            ) : (
              <span className="pill n">Not accepted — training on this org's data stays off</span>
            )}
          </div>

          <div className="grid g2" style={{ marginBottom: 16 }}>
            <div className="card">
              <div className="card-h">
                <h3>Usage by level</h3>
                <span className="tag">This cycle</span>
              </div>
              <div className="card-b">
                {detail.usageByLevel.length === 0 && (
                  <p className="mut sm" style={{ margin: 0 }}>
                    No billed usage yet.
                  </p>
                )}
                {detail.usageByLevel.map((u) => (
                  <div key={u.level} className="krow">
                    {/* "billed items", not "verified": these counts sum every ledger kind for the
                        level (scans, scheduled, sourcing, …), not just verified interviews. */}
                    <span className="k">
                      {u.level} · {u.count.toLocaleString("en-IN")} billed items
                    </span>
                    {/* Credit UNITS, deliberately unformatted as money: since 0031 the usage
                        ledger records credits, and rendering them as ₹ once showed ₹50 where the
                        cash was ~₹3,500. */}
                    <span className="v tnum">{u.credits.toLocaleString("en-IN")} credits</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="card">
              <div className="card-h">
                <h3>Requisitions by state</h3>
              </div>
              <div className="card-b">
                {detail.requisitionsByState.length === 0 && (
                  <p className="mut sm" style={{ margin: 0 }}>
                    No requisitions yet.
                  </p>
                )}
                {detail.requisitionsByState.map((r) => (
                  <div key={r.state} className="krow">
                    <span className="k" style={{ textTransform: "capitalize" }}>
                      {r.state.replace(/_/g, " ")}
                    </span>
                    <span className="v tnum">{r.count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-h">
              <h3>Members</h3>
              <span className="mut sm">One super-admin per org provisions the rest</span>
            </div>
            <div className="tbl-wrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Invite</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.members.length === 0 && (
                    <tr>
                      <td colSpan={5} className="mut sm">
                        No members yet — provision the super-admin below.
                      </td>
                    </tr>
                  )}
                  {detail.members.map((m) => (
                    <tr key={m.userId}>
                      <td style={{ fontWeight: 600 }}>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                          <Users /> {m.name}
                        </span>
                      </td>
                      <td className="mut">{m.email}</td>
                      <td style={{ textTransform: "capitalize" }}>{m.role.replace(/_/g, " ")}</td>
                      <td>
                        <span className={m.status === "active" ? "pill v" : "pill n"}>{m.status}</span>
                      </td>
                      <td>
                        <button
                          className="btn sm ghost"
                          disabled={!isOwner || (resend.isPending && resend.variables === m.email)}
                          title={isOwner ? "Re-send the set-password invite" : "Owner-only"}
                          onClick={() => resend.mutate(m.email)}
                        >
                          {resend.isPending && resend.variables === m.email ? "Resending…" : "Resend invite"}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {(resend.data || resend.isError) && (
              <div style={{ padding: "0 16px 14px" }}>
                {resend.data && (
                  <div className="banner info" style={{ margin: 0 }}>
                    <Info />
                    <div>
                      Invite re-sent to <b>{resend.data.resent.email}</b> — the previous link is now void.
                    </div>
                  </div>
                )}
                {resend.isError && (
                  <div className="banner crit" style={{ margin: 0 }}>
                    <span>{apiErrorText(resend.error, "Couldn't resend the invite. Please retry.")}</span>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="grid g2" style={{ marginBottom: 16 }}>
            <div className="card">
              <div className="card-h">
                <h3>Provision super-admin</h3>
                <span className="tag">Owner</span>
              </div>
              <div className="card-b">
                <div className="field" style={{ marginBottom: 10 }}>
                  <label>Name</label>
                  <input
                    className="input"
                    value={provName}
                    onChange={(e) => setProvName(e.target.value)}
                    placeholder="e.g. Priya Sharma"
                  />
                </div>
                <div className="field" style={{ marginBottom: 12 }}>
                  <label>Email</label>
                  <input
                    className="input"
                    type="email"
                    value={provEmail}
                    onChange={(e) => setProvEmail(e.target.value)}
                    placeholder="owner@company.com"
                  />
                </div>
                {provision.isError && (
                  <div className="banner crit" style={{ marginBottom: 12 }}>
                    <span>{apiErrorText(provision.error, "Couldn't provision the admin. Please retry.")}</span>
                  </div>
                )}
                {provision.data && (
                  <div className="banner info" style={{ marginBottom: 12 }}>
                    <Info />
                    <div>
                      Invite sent to <b>{provision.data.provisioned.email}</b> — they'll set their own
                      password from the emailed link.
                      {isOwner && (
                        <>
                          {" "}
                          <button
                            className="btn sm ghost"
                            style={{ marginLeft: 4 }}
                            disabled={resend.isPending}
                            onClick={() => resend.mutate(provision.data!.provisioned.email)}
                          >
                            {resend.isPending ? "Resending…" : "Resend invite"}
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                )}
                <button
                  className="btn primary"
                  disabled={provName.trim().length === 0 || !provEmail.includes("@") || provision.isPending}
                  onClick={() => provision.mutate()}
                >
                  {provision.isPending ? "Provisioning…" : "Provision & invite"}
                </button>
              </div>
            </div>

            <div className="card">
              <div className="card-h">
                <h3>Act as company</h3>
                {detail.org.supportAccessAllowed ? (
                  <span className="pill v">Allowed · policy opt-in</span>
                ) : (
                  <span className="pill n">Not permitted</span>
                )}
              </div>
              <div className="card-b">
                <p className="mut sm" style={{ marginTop: 0 }}>
                  Impersonation is a privileged support action — time-boxed, scoped to this one org, and every
                  session + action is written to the audit log and disclosed to the org's super-admin.
                </p>
                {detail.org.supportAccessAllowed ? (
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <button className="btn primary" onClick={() => setImpOpen(true)}>
                      <ShieldCheck /> Act as company
                    </button>
                    {isOwner && (
                      <button
                        className="btn ghost sm"
                        disabled={supportAccess.isPending}
                        onClick={() => supportAccess.mutate(false)}
                      >
                        Disable support access
                      </button>
                    )}
                  </div>
                ) : isOwner ? (
                  <button
                    className="btn"
                    disabled={supportAccess.isPending}
                    onClick={() => supportAccess.mutate(true)}
                  >
                    {supportAccess.isPending ? "Enabling…" : "Enable support access"}
                  </button>
                ) : (
                  <button className="btn" disabled>
                    Not permitted — an Owner must enable support access
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-h">
              <h3>Credits</h3>
              <span className="tag">Owner grant</span>
            </div>
            <div className="card-b">
              <div className="krow">
                <span className="k">Spendable balance</span>
                <span className="tnum" style={{ fontWeight: 600 }}>{wallet.toLocaleString("en-IN")} cr</span>
              </div>
              <p className="mut sm" style={{ margin: "10px 0" }}>
                A grant is credits nobody paid for — it spends exactly like a purchase but never
                appears in revenue. Use it for pilots and testing; a paying customer should buy.
                A negative number takes credits back. Every grant is audited.
              </p>
              <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
                <div className="field" style={{ margin: 0, maxWidth: 180 }}>
                  <label>Credits to grant</label>
                  <input
                    type="number"
                    value={grantUnits}
                    placeholder="e.g. 200"
                    onChange={(e) => setGrantUnits(e.target.value)}
                  />
                </div>
                <button
                  className="btn primary"
                  disabled={!grantValid || grant.isPending}
                  onClick={() => grant.mutate(grantN)}
                >
                  {grant.isPending ? "Granting…" : grantN < 0 ? "Take back" : "Grant credits"}
                </button>
              </div>
              {grant.isError && (
                <div className="banner crit" style={{ marginTop: 12 }}>
                  <Alert />
                  <span>{apiErrorText(grant.error, "Couldn't grant credits.")}</span>
                </div>
              )}
              {grant.isSuccess && (
                <p className="mut sm" style={{ marginBottom: 0, marginTop: 10 }}>
                  Done — balance is now {grant.data.balance.toLocaleString("en-IN")} cr.
                </p>
              )}
            </div>
          </div>

          {detail.org.allowances && (
            <div className="card" style={{ marginBottom: 16 }}>
              <div className="card-h">
                <h3>Plan allowances</h3>
                <span className="tag">Owner grant</span>
              </div>
              <div className="card-b">
                {(Object.keys(ALLOWANCE_LABEL) as AllowanceUnit[]).map((u) => {
                  const a = detail.org.allowances![u];
                  return (
                    <div className="krow" key={u}>
                      <span className="k">{ALLOWANCE_LABEL[u]}</span>
                      <span className="tnum" style={{ fontWeight: 600 }}>
                        {a.remaining.toLocaleString("en-IN")} left
                        <span className="mut" style={{ fontWeight: 400 }}>
                          {" "}
                          · {a.used.toLocaleString("en-IN")} of {a.total.toLocaleString("en-IN")} used
                        </span>
                      </span>
                    </div>
                  );
                })}
                <p className="mut sm" style={{ margin: "10px 0" }}>
                  Adds jobs, resumes or AI interviews nobody paid for — they work exactly like a plan's
                  and expire on the normal credit validity. A negative number takes them back. Every
                  grant is audited.
                </p>
                <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
                  <div className="field" style={{ margin: 0, maxWidth: 180 }}>
                    <label>What</label>
                    <select value={allowUnit} onChange={(e) => setAllowUnit(e.target.value as AllowanceUnit)}>
                      {(Object.keys(ALLOWANCE_LABEL) as AllowanceUnit[]).map((u) => (
                        <option key={u} value={u}>
                          {ALLOWANCE_LABEL[u]}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="field" style={{ margin: 0, maxWidth: 140 }}>
                    <label>How many</label>
                    <input
                      type="number"
                      value={allowQty}
                      placeholder="e.g. 3"
                      onChange={(e) => setAllowQty(e.target.value)}
                    />
                  </div>
                  <button
                    className="btn primary"
                    disabled={!allowValid || allowGrant.isPending}
                    onClick={() => allowGrant.mutate({ unit: allowUnit, qty: allowN })}
                  >
                    {allowGrant.isPending ? "Saving…" : allowN < 0 ? "Take back" : "Grant"}
                  </button>
                </div>
                {allowGrant.isError && (
                  <div className="banner crit" style={{ marginTop: 12 }}>
                    <Alert />
                    <span>{apiErrorText(allowGrant.error, "Couldn't change the allowance.")}</span>
                  </div>
                )}
                {allowGrant.isSuccess && (
                  <p className="mut sm" style={{ marginBottom: 0, marginTop: 10 }}>
                    {allowGrant.data.changed === 0
                      ? "Nothing to take back."
                      : `Done — ${allowGrant.data.changed > 0 ? "added" : "removed"} ${Math.abs(allowGrant.data.changed)}.`}
                  </p>
                )}
              </div>
            </div>
          )}

          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-h">
              <h3>Ranking basis</h3>
              <span className="tag">what this org's candidate lists, buckets and headline rank by</span>
            </div>
            <div className="card-b" style={{ display: "grid", gap: 10 }}>
              {(["interview", "competency"] as RankingBasis[]).map((b) => {
                const current = detail.org.rankingBasis ?? "interview";
                return (
                  <label key={b} style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: rankingBasisMut.isPending ? "wait" : "pointer" }}>
                    <input
                      type="radio"
                      name="ranking-basis"
                      checked={current === b}
                      disabled={rankingBasisMut.isPending}
                      onChange={() => rankingBasisMut.mutate(b)}
                      style={{ marginTop: 4 }}
                    />
                    <span>
                      <b>{RANKING_BASIS_LABEL[b]}</b>
                      {b === "interview"
                        ? " — rank by the question-level score: each answer scored 0–100 from the role and that question alone, averaged. Ties break on the competency score."
                        : " — rank by capability and coverage from the competency evaluation. Ties break on the interview score."}
                    </span>
                  </label>
                );
              })}
              <span className="mut sm">
                Both scores are always shown on every report. Switching re-orders lists, filters and the headline immediately — nothing is re-scored,
                no model is called. The change is audited.
              </span>
              {rankingBasisMut.isError && (
                <div className="banner crit">
                  <Alert />
                  <span>{apiErrorText(rankingBasisMut.error, "Couldn't change the ranking basis.")}</span>
                </div>
              )}
            </div>
          </div>

          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-h">
              <h3>Feature flags</h3>
              <span className="tag">org-facing capabilities — one switch each, applies to every requisition</span>
            </div>
            <div className="card-b" style={{ display: "grid", gap: 10 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
                <Switch
                  on={flags.rescoreEnabled}
                  disabled={flagsMut.isPending}
                  onToggle={() => flagsMut.mutate({ rescoreEnabled: !flags.rescoreEnabled })}
                />
                <span>
                  <b>Rescore</b> — lets this org trigger a real re-score of a candidate's CV from their own report page (uses one resume scan, audited)
                </span>
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
                <Switch
                  on={flags.customPromptEnabled}
                  disabled={flagsMut.isPending}
                  onToggle={() =>
                    flagsMut.mutate({ customPromptEnabled: !flags.customPromptEnabled })
                  }
                />
                <span>
                  <b>Custom judge prompt</b> — lets a hiring manager/org admin edit any of this org's
                  requisitions' own judge prompt (copied from the platform default)
                </span>
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
                <Switch
                  on={flags.copyQuestionsEnabled}
                  disabled={flagsMut.isPending}
                  onToggle={() =>
                    flagsMut.mutate({ copyQuestionsEnabled: !flags.copyQuestionsEnabled })
                  }
                />
                <span>
                  <b>Copy transcript button</b> — on by default (PR #59); turn off to hide it for this
                  org only
                </span>
              </label>
              {flagsMut.isError && (
                <div className="banner crit">
                  <Alert />
                  <span>{apiErrorText(flagsMut.error, "Couldn't update flags.")}</span>
                </div>
              )}
            </div>
          </div>

          {orgKind === "end_customer" && (
            <div className="card" style={{ marginBottom: 16 }}>
              <div className="card-h">
                <h3>Staff augmentation</h3>
                <span className="tag">Contractor placement</span>
              </div>
              <div className="card-b">
                <p className="mut sm" style={{ marginTop: 0 }}>
                  Most customers hire directly and never place a contractor, so this is off unless you turn
                  it on. While it is off this org sees no staff-aug controls anywhere, and the API refuses a
                  staff-aug requisition even if one is hand-rolled.
                </p>
                <label
                  style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14, cursor: "pointer" }}
                >
                  <input
                    type="checkbox"
                    checked={staffAug}
                    disabled={staffAugMut.isPending}
                    onChange={(e) => staffAugMut.mutate(e.target.checked)}
                    style={{ width: 16, height: 16 }}
                  />
                  <span style={{ fontWeight: 600 }}>
                    This organisation places contractors through staffing vendors
                  </span>
                </label>
                {staffAugMut.isError && (
                  <div className="banner crit" style={{ marginBottom: 12 }}>
                    <Alert />
                    <span>{apiErrorText(staffAugMut.error, "Couldn't change the entitlement.")}</span>
                  </div>
                )}
                {!staffAug ? (
                  <p className="mut sm" style={{ margin: 0 }}>
                    Turn it on to link the vendor firms that will fulfil this customer's staff-aug requisitions.
                  </p>
                ) : (
                <>
                <p className="mut sm" style={{ marginTop: 0 }}>
                  Vendor firms pre-linked to this end customer. A linked vendor's recruiters can be assigned
                  staff-aug requisitions to source and interview for; the end customer still makes every hiring
                  decision.
                </p>
                {vendorsQuery.isLoading && (
                  <p className="mut sm" style={{ margin: 0 }}>
                    Loading linked vendors…
                  </p>
                )}
                {!vendorsQuery.isLoading && linkedVendors.length === 0 && (
                  <p className="mut sm" style={{ margin: 0 }}>
                    No vendors linked yet — link one below to enable staff-aug fulfilment.
                  </p>
                )}
                {linkedVendors.map((v) => (
                  <div key={v.id} className="krow">
                    <span className="k" style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                      <Users /> {v.name}
                    </span>
                    <Link className="btn sm ghost" to={`/admin/orgs/${v.id}`}>
                      Open
                    </Link>
                  </div>
                ))}

                <div
                  style={{ display: "flex", gap: 8, alignItems: "flex-end", marginTop: 14 }}
                >
                  <div className="field" style={{ flex: 1, margin: 0 }}>
                    <label>Link a vendor</label>
                    <select value={pickVendorId} onChange={(e) => setPickVendorId(e.target.value)}>
                      <option value="">Select a vendor org…</option>
                      {vendorOptions.map((o) => (
                        <option key={o.id} value={o.id}>
                          {o.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <button
                    className="btn primary"
                    disabled={pickVendorId.length === 0 || linkVendor.isPending}
                    onClick={() => linkVendor.mutate(pickVendorId)}
                  >
                    {linkVendor.isPending ? "Linking…" : "Link vendor"}
                  </button>
                </div>
                {orgsQuery.data && vendorOptions.length === 0 && (
                  <p className="mut sm" style={{ marginBottom: 0 }}>
                    No unlinked vendor orgs available — create a vendor-kind org first.
                  </p>
                )}
                {linkVendor.isError && (
                  <div className="banner crit" style={{ marginTop: 12 }}>
                    <span>{apiErrorText(linkVendor.error, "Couldn't link the vendor. Please retry.")}</span>
                  </div>
                )}
                </>
                )}
              </div>
            </div>
          )}

          {isVendorOrg && (
            <div className="card" style={{ marginBottom: 16 }}>
              <div className="card-h">
                <h3>Clients this vendor fulfils for</h3>
                <span className="tag">Staff-aug fulfilment</span>
              </div>
              <div className="card-b">
                {linkedClients.length === 0 && (
                  <p className="mut sm" style={{ margin: 0 }}>
                    Not linked to any end customers yet. A platform admin links this vendor from the end
                    customer's org page.
                  </p>
                )}
                {linkedClients.map((c) => (
                  <div key={c.id} className="krow">
                    <span className="k" style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                      <Users /> {c.name}
                    </span>
                    <Link className="btn sm ghost" to={`/admin/orgs/${c.id}`}>
                      Open
                    </Link>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Per requirement → per candidate dated log (adminActivity.ts) */}
          <OrgActivityLog orgId={orgId} />

          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-h">
              <h3>Recent activity</h3>
              <span className="tag">Live</span>
            </div>
            <div className="card-b">
              {detail.recentActivity.length === 0 && (
                <p className="mut sm" style={{ margin: 0 }}>
                  Nothing yet.
                </p>
              )}
              {detail.recentActivity.map((a, i) => (
                <div key={`${a.ts}-${i}`} className="krow">
                  <span className="k">{a.label}</span>
                  <span className="v mut sm tnum">{new Date(a.ts).toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="disclaimer">
            <b>Operator view.</b> Health, billing & margin metadata only — candidate content stays inside the
            tenant. Tenant isolation is enforced at the data layer.
          </div>

          {impOpen && (
            <Modal title={`Act as ${detail.org.name}`} onClose={() => setImpOpen(false)}>
              <p className="mut sm" style={{ marginTop: 0 }}>
                A scoped, time-boxed token is issued for this one org. A reason is mandatory and the session is
                audited end-to-end.
              </p>
              <div className="field" style={{ marginBottom: 10 }}>
                <label>Reason (required)</label>
                <textarea
                  className="input"
                  rows={3}
                  value={impReason}
                  onChange={(e) => setImpReason(e.target.value)}
                  placeholder="e.g. Support ticket #482 — requisition stuck in screening"
                />
              </div>
              <div className="field" style={{ marginBottom: 12 }}>
                <label>Duration</label>
                <select value={impDuration} onChange={(e) => setImpDuration(Number(e.target.value))}>
                  <option value={15}>15 minutes</option>
                  <option value={30}>30 minutes</option>
                  <option value={60}>60 minutes</option>
                </select>
              </div>
              {impersonate.isError && (
                <div className="banner crit" style={{ marginBottom: 12 }}>
                  <Alert />
                  <span>{apiErrorText(impersonate.error, "Couldn't start the session. Please retry.")}</span>
                </div>
              )}
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <button className="btn ghost" onClick={() => setImpOpen(false)}>
                  Cancel
                </button>
                <button
                  className="btn primary"
                  disabled={impReason.trim().length < 4 || impersonate.isPending}
                  onClick={() => impersonate.mutate()}
                >
                  {impersonate.isPending ? "Starting…" : "Start scoped session"}
                </button>
              </div>
            </Modal>
          )}
        </>
      )}
    </section>
  );
}
